import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { isMobileBreakpoint, onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onSummaryClick);
    this.addEventListener('toggle', this.#onAccordionToggle, true);
    document.addEventListener('pointerdown', this.#onDocumentPointerDown, true);
    window.addEventListener('resize', this.#onResize);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onSummaryClick);
    this.removeEventListener('toggle', this.#onAccordionToggle, true);
    document.removeEventListener('pointerdown', this.#onDocumentPointerDown, true);
    window.removeEventListener('resize', this.#onResize);
  }

  /**
   * Take the close of a panel accordion away from the browser, so the row is
   * still open while it collapses.
   *
   * ocs.ca does the same thing (`js_theme.js:1060`): the summary click's default
   * action is cancelled, the box animates to a zero height while it is still
   * displayed, and `display: none` lands 350ms later once there is nothing left
   * to see. Left to the browser, our close instead depends on
   * `content-visibility … allow-discrete` holding the content on screen while it
   * shrinks — which is a mechanism production has no counterpart for rather than
   * a match to it.
   *
   * Only the close is intercepted. An open is the browser's to do, and
   * `#onAccordionToggle` picks it up from there.
   *
   * @param {Event} event
   */
  #onSummaryClick = (event) => {
    const summary = event.target instanceof Element ? event.target.closest('summary') : null;
    const details = summary?.parentElement;

    if (!(details instanceof HTMLDetailsElement)) return;
    if (details.parentElement?.localName !== 'accordion-custom') return;

    // Panel accordions only — the same scope the stylesheet's clamp has. The
    // shallow menu's top-level accordion is nobody's here and stays the
    // browser's to open and close.
    if (!clampedContent(details)) return;

    // Tapping again while it is collapsing puts the row back, rather than
    // queueing a second close behind the first.
    if (cancelCollapse(details)) {
      event.preventDefault();
      summary?.setAttribute('aria-expanded', 'true');
      return;
    }

    if (!details.open) return;

    event.preventDefault();
    collapseAccordion(details);
  };

  /**
   * Re-measure the open rows when the viewport changes size.
   *
   * A height measured at 390px wide is wrong the moment the phone is rotated:
   * the row would cut its last item off, or leave a gap under it. Production
   * clears the value instead (`.css('max-height','')`), which it can afford
   * because its resize handler only runs when the accordion is being dismantled
   * for desktop. Ours has to keep the row correct where it is, so it measures
   * again rather than letting go.
   *
   * A closed row is left with whatever it last measured. That value is only ever
   * the target for the window before the next expand re-measures in the same
   * task, so a stale one is never rendered.
   */
  #onResize = () => {
    for (const details of this.querySelectorAll('.menu-drawer__submenu accordion-custom > details[open]')) {
      if (details instanceof HTMLDetailsElement) expandAccordion(details);
    }
  };

  /**
   * Collapse the sibling categories when one is expanded, so a panel shows one
   * open category at a time rather than stacking four of them off the bottom
   * of the screen.
   *
   * Listening in the capture phase from the host, rather than on each
   * accordion: `toggle` does not bubble, so a delegated listener has no other
   * phase available to it. That also means panels injected later are covered
   * without re-binding.
   *
   * The markup's own `name` attribute is the platform's answer to this and was
   * what this started as, but it did not take effect on the drawer in practice.
   * This does not depend on that support.
   *
   * Also where a panel accordion's measured height is written, since this is the
   * one place that sees every open, whoever caused it. See `clampedContent` at
   * the bottom of the file for what that is for.
   *
   * Only fires on opening. The closes below re-enter with `open === false` and
   * stop at the first guard, as do `resetAccordions` and `accordion-custom`'s
   * default-state pass, so there is no cascade.
   *
   * @param {Event} event
   */
  #onAccordionToggle = (event) => {
    const details = event.target;

    if (!(details instanceof HTMLDetailsElement) || !details.open) return;

    // Scoped to the category accordions. The drawer's own <details> and the
    // language dropdown are disclosures too, and neither has siblings to close.
    if (details.parentElement?.localName !== 'accordion-custom') return;

    expandAccordion(details);

    const group = accordionGroup(details);

    if (!group) return;

    for (const other of this.querySelectorAll('accordion-custom > details[open]')) {
      if (!(other instanceof HTMLDetailsElement) || other === details) continue;
      if (accordionGroup(other) !== group) continue;

      // Through the same collapse the tapped row's own close goes through, so a
      // displaced sibling animates out instead of vanishing. Production does
      // this too: its open path returns every other accordion's max-height to
      // the stylesheet's zero and only then hides them, on the same timer.
      collapseAccordion(other);
    }
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Close mobile drawer language dropdowns when the pointer starts outside of them.
   * @param {PointerEvent} event
   */
  #onDocumentPointerDown = (event) => {
    const openLanguageDetails = this.querySelectorAll('.menu-drawer__language-details[open]');

    if (!openLanguageDetails.length) return;

    const path = typeof event.composedPath === 'function' ? event.composedPath() : [];

    openLanguageDetails.forEach((details) => {
      const pointerStartedInsideDetails = path.length
        ? path.includes(details)
        : event.target instanceof Node && details.contains(event.target);

      if (!pointerStartedInsideDetails) {
        this.#closeLanguageDetails(details);
      }
    });
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    if (details === this.refs.details) {
      this.#closeOpenLanguageDetails();
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);

        // A panel used to remember what was expanded inside it, so backing out
        // of an L2 and going straight back in reopened whatever had been left
        // showing. Closing a panel returns the accordions it contains — and
        // closing the drawer, every accordion in it — to their declared state.
        // `preventInitialAccordionAnimations` already runs on open, so a panel
        // that comes back collapsed does not animate its way there.
        resetAccordions(details === this.refs.details ? this : details);

        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Close every open language dropdown inside this mobile drawer.
   */
  #closeOpenLanguageDetails() {
    const openLanguageDetails = this.querySelectorAll('.menu-drawer__language-details[open]');

    openLanguageDetails.forEach((details) => this.#closeLanguageDetails(details));
  }

  /**
   * Close a language dropdown without invoking drawer focus or submenu behavior.
   * @param {Element} details
   */
  #closeLanguageDetails(details) {
    details.removeAttribute('open');
    details.querySelector('summary')?.removeAttribute('aria-expanded');
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');

        // Seed a height for any row that has never been measured, so that its
        // first expand animates like every one after it. Only a row with no
        // value of its own — a previous measurement is a better starting target
        // than this is, and is not rendered either way.
        if (!element.style.getPropertyValue(L3_HEIGHT)) {
          element.style.setProperty(L3_HEIGHT, L3_HEIGHT_SEED);
        }
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

/**
 * The list a category accordion belongs to — the parent `<ul>` of its row.
 *
 * Grouping by the list is what keeps a tap under Products from closing a
 * category under Learn. Every panel's categories are in the DOM from the start,
 * each set in its own `menu-drawer__menu--childlist`, so "the siblings" has to
 * mean the ones in the same list and not every accordion in the drawer.
 *
 * Returns null for a `<details>` outside a list row, which is how the drawer's
 * own disclosure and the language dropdown fall out of the grouping rather than
 * being named as exceptions.
 *
 * @param {HTMLDetailsElement} details
 * @returns {Element | null}
 */
function accordionGroup(details) {
  return details.closest('li')?.parentElement ?? null;
}

/**
 * The list a panel accordion grows and shrinks, or null if this accordion is
 * not one the drawer clamps.
 *
 * ocs.ca animates its mobile nav accordion to a pixel `max-height` it measures
 * when the row is tapped:
 *
 *   var contentHeight = $(this).find('.accordionize__content > *').outerHeight(true);
 *   $(this).find('.accordionize__content').addClass('accordion--show').css('max-height', contentHeight);
 *
 * Horizon interpolates `block-size: 0 -> auto` instead, which re-resolves the
 * row's layout on every frame of the 0.3s. The CSS half of the swap is in
 * snippets/header-drawer.liquid; this is the measuring.
 *
 * Scoped to `.menu-drawer__submenu` for the same reason the CSS is: the shallow
 * menu (`use_nested_accordions`) puts an accordion at the top level of the
 * drawer too, and that one is still on Horizon's interpolation. An inline clamp
 * written onto a box no rule clamps would seal it shut.
 *
 * @param {HTMLDetailsElement} details
 * @returns {HTMLElement | null}
 */
function clampedContent(details) {
  if (!details.closest('.menu-drawer__submenu')) return null;

  const content = details.querySelector(':scope > .details-content');

  return content instanceof HTMLElement ? content : null;
}

/**
 * The height `content` needs when open, in px.
 *
 * Measured across the rows rather than by lifting the clamp off the container
 * and reading it back. Reading it back forces a style recalculation, which makes
 * `max-block-size: none` the value the transition starts from — and `none` does
 * not interpolate, so the row would snap open. Production has the same
 * constraint and answers it the same way, measuring the content's children
 * instead of the content box.
 *
 * The span from the topmost row to the bottommost, rather than a sum of them, so
 * that margins collapsed between rows, flex gaps, and the grid variant's second
 * column are all accounted for without being special-cased. A margin on the
 * leading or trailing row would not be — there is none today, and this is where
 * to add it.
 *
 * @param {HTMLElement} content
 * @returns {number}
 */
function accordionContentHeight(content) {
  let top = Infinity;
  let bottom = -Infinity;

  for (const row of content.children) {
    const rect = row.getBoundingClientRect();

    top = Math.min(top, rect.top);
    bottom = Math.max(bottom, rect.bottom);
  }

  if (!Number.isFinite(top)) return 0;

  // The container's own padding is outside that span. It is settled by the time
  // this runs — the drawer's override takes `padding-block` out of the
  // transition precisely so this read is not a moving target.
  const styles = getComputedStyle(content);

  return bottom - top + (parseFloat(styles.paddingBlockStart) || 0) + (parseFloat(styles.paddingBlockEnd) || 0);
}

/** The property the drawer's `[open]` rule reads the measured height from. */
const L3_HEIGHT = '--menu-drawer-l3-height';

/**
 * A stand-in height, written before any row has been measured.
 *
 * `[open]` lands before this script can measure, so whatever CSS resolves to in
 * that window is where the transition starts. Left to the stylesheet's own
 * fallback that is `none`, which does not interpolate to a pixel value, so the
 * row's first expand snapped open while every one after it — seeded by the
 * previous measurement — animated.
 *
 * A coarse pixel value closes that gap. `expandAccordion` retargets it in the
 * same task, so no frame is ever rendered against it; it exists only so that the
 * value in the window is interpolable. The stylesheet keeps `none` as its own
 * fallback for a drawer whose script never ran, which is why this is seeded here
 * rather than written into the CSS.
 */
const L3_HEIGHT_SEED = '100dvh';

/**
 * Clamp a panel accordion to the height of its rows, so it grows to a pixel
 * value instead of interpolating `block-size: auto`.
 *
 * @param {HTMLDetailsElement} details
 */
function expandAccordion(details) {
  const content = clampedContent(details);

  if (!content) return;

  // An expand always wins over a collapse still on the clock. Leaving the class
  // on would hold the row clamped at zero while `[open]` says it is showing.
  cancelCollapse(details);

  const height = accordionContentHeight(content);

  // A row inside a panel that is not on screen yet measures nothing —
  // `resetAccordions` can reopen one while its panel is still closed. Dropping
  // the property hands the row to the stylesheet's `none`, which shows it
  // unanimated rather than sealing it at zero.
  if (height > 0) content.style.setProperty(L3_HEIGHT, `${height}px`);
  else content.style.removeProperty(L3_HEIGHT);
}

/** Marks a row that is collapsing but has not been closed yet. */
const COLLAPSE_CLASS = 'menu-drawer__accordion--collapsing';

/**
 * How long the row stays open while it collapses.
 *
 * Production's own comment on the same number: "this timeout should be greater
 * than the CSS transition time which is currently 0.3s". The stylesheet's clamp
 * is on that same 0.3s, so this is production's 350ms unchanged. The row sits
 * collapsed-but-open for the last 50ms, which shows nothing: it is already at a
 * zero height and clipped.
 */
const COLLAPSE_MS = 350;

/** @type {WeakMap<HTMLDetailsElement, ReturnType<typeof setTimeout>>} */
const pendingCollapses = new WeakMap();

/**
 * Close a panel accordion the way ocs.ca does — animate it shut first, then
 * take `[open]` off it.
 *
 * The row keeps `[open]` for the duration, which is what the stylesheet's
 * `.menu-drawer__accordion--collapsing` clamp is for. Nothing about the collapse
 * then depends on the browser holding hidden content painted while it shrinks.
 *
 * `aria-expanded` goes false at once rather than at the end. The row is on its
 * way out from the moment it is tapped, and production reports it the same way.
 *
 * @param {HTMLDetailsElement} details
 */
function collapseAccordion(details) {
  const summary = details.querySelector('summary');

  summary?.setAttribute('aria-expanded', 'false');

  // Not one of the rows the stylesheet clamps, so there is no collapse to
  // animate and nothing to wait for.
  if (!clampedContent(details)) {
    details.open = false;
    return;
  }

  // Already on its way out. A second call — a sibling sweep landing on a row the
  // visitor had just tapped shut — must not restart the timer.
  if (pendingCollapses.has(details)) return;

  details.classList.add(COLLAPSE_CLASS);

  pendingCollapses.set(
    details,
    setTimeout(() => {
      pendingCollapses.delete(details);
      details.classList.remove(COLLAPSE_CLASS);
      details.open = false;
    }, COLLAPSE_MS)
  );
}

/**
 * Abandon a collapse that has not finished, leaving the row open.
 *
 * Every path that reopens a row has to come through here first, or it reopens
 * one the stylesheet is still clamping to zero — open by every measure except
 * the one the visitor can see.
 *
 * @param {HTMLDetailsElement} details
 * @returns {boolean} Whether there was one to abandon.
 */
function cancelCollapse(details) {
  const pending = pendingCollapses.get(details);

  if (pending === undefined) return false;

  clearTimeout(pending);
  pendingCollapses.delete(details);
  details.classList.remove(COLLAPSE_CLASS);

  return true;
}

/**
 * Return every accordion under `root` to the state it declares.
 *
 * Deliberately not a blanket close. `accordion-custom` reads
 * `open-by-default-on-mobile` / `-on-desktop` in its own connectedCallback, so
 * for those the reset state is open, not collapsed — the drawer's two-level
 * layout uses one to expand the first group. Everything the visitor opened by
 * hand has no such attribute and closes.
 *
 * The `open` attribute is the accordion's entire state; it keeps nothing of its
 * own, so setting it here is enough and no notification is owed.
 *
 * @param {Element} root
 *
 * A collapse still on the clock is abandoned first, whichever way this leaves
 * the row. Left alone, one on a row this wants open would keep the row clamped
 * to nothing and then close it when its timer came up.
 *
 * @param {Element} root
 */
function resetAccordions(root) {
  const attribute = isMobileBreakpoint() ? 'open-by-default-on-mobile' : 'open-by-default-on-desktop';

  for (const details of root.querySelectorAll('accordion-custom > details')) {
    if (!(details instanceof HTMLDetailsElement)) continue;

    cancelCollapse(details);

    const open = details.parentElement?.hasAttribute(attribute) ?? false;

    if (details.open === open) continue;

    details.open = open;
    details.querySelector('summary')?.setAttribute('aria-expanded', String(open));
  }
}
